import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { useCreateUser } from "hooks/mutations/user";
import { getStaffLookup, StaffLookupResult } from "hooks/queries/user";
import { useState } from "react";
import { translate, translateWithVars } from "shared/languages";
import { RegisterUserForm } from "shared/types";
import { Gender, UserType } from "shared/types/enum";
import { DEFAULT_DATE_FORMAT, now } from "shared/utils/dayjs";
import { formatPhone } from "shared/utils/phone";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";

const { Option } = Select;

// "Tambah Instruktur" / "Tambah Staf": one modal for both employee types.
// The type comes from the Karyawan list the button sits on (Instruktur
// teaches and signs in to the instructor app; Staf is non-teaching with no
// app access).
//  Instruktur: NewCustomerModal's phone-first shape. The phone is the login
//     identity, so it is looked up before anything is typed.
//  Staf: the full form straight away (the phone is optional, so a phone-only
//     first step would mostly be an empty click). If a phone WAS entered, the
//     same lookup runs on Add, so an existing person is still linked rather
//     than duplicated.
//  Either way a phone is looked up scoped to this studio and branched on the
//  status AND the person's roles, the same rule the server enforces
//  (staffRoleConflict in createUserByBusinessId):
//     - staff of another studio            -> blocked (staff is single-studio).
//     - already holds the requested type   -> blocked.
//     - an instructor, adding Staf         -> blocked (already on payroll).
//     - an admin here without the role     -> confirm, the role is added.
//     - a Staf here, adding Instruktur     -> confirm (promotion; the server
//       mints the instructor code and link).
//     - a person exists but is linkable (a client, or deactivated staff
//       here) -> confirm, then link/reactivate; a deactivated former
//       instructor comes back WITH instructor access, so the confirm says so.
//     - nobody                             -> create (Instruktur: show the
//       form first).
//  With no phone (Staf only) there is nothing to look up: created as typed.
export type KaryawanType = UserType.Instructor | UserType.Staff;
type Step = "phone" | "create" | "confirm";

const typeLabel = (type: KaryawanType) =>
  translate(type === UserType.Instructor ? "karyawan_type.instructor" : "karyawan_type.staff");

const NewKaryawanModal = ({ type: karyawanType }: { type: KaryawanType }) => {
  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialStep: Step = karyawanType === UserType.Staff ? "create" : "phone";
  const [step, setStep] = useState<Step>(initialStep);
  const [pendingPhone, setPendingPhone] = useState("");
  const [existing, setExisting] = useState<StaffLookupResult | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const { mutate: createUser, isPending: isCreating } = useCreateUser();

  const isStaf = karyawanType === UserType.Staff;
  const close = () => setIsModalOpen(false);
  const reset = () => {
    form.resetFields();
    setStep(initialStep);
    setPendingPhone("");
    setExisting(null);
    setLookupLoading(false);
  };

  // Step 1 -> classify the phone and branch. Returns an error key when the
  // person cannot be added as the chosen type, null when the flow may go on.
  const conflictKey = (result: StaffLookupResult): string | null => {
    const roles = result.roles ?? [];
    if (result.status === "staff_elsewhere") return "messages.error.instructor_staff_elsewhere";
    if (result.status === "active_staff") {
      // An active instructor here.
      return isStaf ? "messages.error.staff_already_instructor" : "messages.error.instructor_already_staff";
    }
    if (result.status === "admin_here" && isStaf && roles.includes(UserType.Staff)) {
      return "messages.error.staff_already_staff";
    }
    return null;
  };

  // Look a phone up and decide what happens next: a conflict (shown as a
  // toast, flow stays put), an existing person (confirm step), or "new".
  // Shared by the Instruktur phone step and the Staf form's Add.
  const classifyPhone = async (
    phone_number: string
  ): Promise<"conflict" | "existing" | "new"> => {
    setLookupLoading(true);
    try {
      const result = await getStaffLookup(phone_number);
      const conflict = conflictKey(result);
      if (conflict) {
        message.error(translate(conflict));
        return "conflict";
      }
      setPendingPhone(phone_number);
      if (result.status === "new") {
        setExisting(null);
        return "new";
      }
      setExisting(result);
      setStep("confirm");
      return "existing";
    } catch {
      message.error(translate("messages.error.general"));
      return "conflict";
    } finally {
      setLookupLoading(false);
    }
  };

  // Instruktur step 1 -> classify the phone and branch.
  const onContinue = () => {
    form
      .validateFields(["phone_number"])
      .then(async ({ phone_number }) => {
        if ((await classifyPhone(phone_number)) === "new") setStep("create");
      })
      .catch(() => {
        // AntD already paints the inline phone error.
      });
  };

  const submitCreate = (values: Record<string, unknown>, phone: string) => {
    const { phone_number: _ignored, ...rest } = values;
    createUser(
      {
        ...rest,
        ...(phone ? { phone_number: phone } : {}),
        user_type: karyawanType,
      } as RegisterUserForm,
      { onSuccess: close }
    );
  };

  // Create a brand-new person. For an Instruktur the phone was already
  // classified on step 1. For a Staf this is the first time the phone is
  // seen: with one, it is looked up now (an existing person switches to the
  // confirm step instead of a duplicate); with none, created as typed.
  const onCreate = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (!isStaf) {
          submitCreate(values, pendingPhone);
          return;
        }
        const phone = (values.phone_number as string | undefined) ?? "";
        if (!phone) {
          submitCreate(values, "");
          return;
        }
        if ((await classifyPhone(phone)) === "new") submitCreate(values, phone);
      })
      .catch(() => {
        // AntD already paints inline errors.
      });
  };

  // Step 2b -> add the type to an existing person. No name fields are sent;
  // the server reuses the stored identity (createUserByBusinessId's "person
  // already exists" branch) and pushes the role.
  const onConfirmAdd = () => {
    createUser(
      { phone_number: pendingPhone, user_type: karyawanType } as RegisterUserForm,
      { onSuccess: close }
    );
  };

  const confirmCopy = (() => {
    if (!existing) return null;
    const roles = existing.roles ?? [];
    const adminWithoutInstructor =
      existing.status === "admin_here" && !isStaf && roles.includes(UserType.Admin);
    const primary = adminWithoutInstructor
      ? translate("messages.info.admin_instructor_link")
      : translateWithVars("messages.info.existing_person_add_as", { type: typeLabel(karyawanType) });
    const deactivatedInstructor =
      existing.status === "known" && !!existing.deactivated_here && roles.includes(UserType.Instructor);
    return { primary, deactivatedInstructor };
  })();

  const footer = (() => {
    if (step === "phone") {
      return (
        <Flex justify="end" gap={10}>
          <Button onClick={close}>{translate("button.cancel")}</Button>
          <Button type="primary" loading={lookupLoading} onClick={onContinue}>
            {translate("button.continue")}
          </Button>
        </Flex>
      );
    }
    if (step === "create") {
      return (
        <Flex justify="end" gap={10}>
          {isStaf ? (
            <Button onClick={close}>{translate("button.cancel")}</Button>
          ) : (
            <Button onClick={() => setStep("phone")}>{translate("button.back")}</Button>
          )}
          <Button type="primary" loading={isCreating || lookupLoading} onClick={onCreate}>
            {translate("button.add")}
          </Button>
        </Flex>
      );
    }
    return (
      <Flex justify="end" gap={10}>
        {/* Back to where the number was typed: the Staf form keeps what was
            typed (AntD preserves unmounted field values). */}
        <Button onClick={() => setStep(isStaf ? "create" : "phone")}>{translate("button.change_number")}</Button>
        <Button type="primary" loading={isCreating} onClick={onConfirmAdd}>
          {translate("button.confirm_add")}
        </Button>
      </Flex>
    );
  })();

  return (
    <>
      <Button onClick={() => setIsModalOpen(true)} type="primary" icon={<PlusOutlined />}>
        {translate("button.add")} {typeLabel(karyawanType)}
      </Button>
      <Modal
        title={translate(isStaf ? "title.create_staff" : "title.create_instructor")}
        open={isModalOpen}
        onCancel={close}
        afterClose={reset}
        footer={<ConfigProvider theme={modalButtonsTheme}>{footer}</ConfigProvider>}
      >
        <Form form={form} layout="vertical">
          {/* Phone is always present. Instruktur: editable on step 1 only,
              locked context afterwards. Staf: editable on the form, locked on
              the confirm step. Required for an Instruktur, optional for a Staf. */}
          <Form.Item
            name="phone_number"
            label={translate("label.phone_number")}
            rules={phoneRules({ required: !isStaf })}
          >
            <PhoneInput
              disabled={isStaf ? step === "confirm" : step !== "phone"}
              placeholder={`${translate("form.placeholders.enter")} ${translate(
                "form.placeholders.phone_number"
              )}`}
            />
          </Form.Item>

          {step === "create" && (
            <>
              <Form.Item
                name="first_name"
                label={translate("label.first_name")}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.input")} ${translate(
                      "label.first_name"
                    )} ${translate("form.error.input_end")}`,
                  },
                ]}
              >
                <Input placeholder={`${translate("form.placeholders.enter")} ${translate("label.first_name")}`} />
              </Form.Item>

              <Form.Item name="middle_name" label={translate("label.middle_name")}>
                <Input placeholder={`${translate("form.placeholders.enter")} ${translate("label.middle_name")}`} />
              </Form.Item>

              <Form.Item name="last_name" label={translate("label.last_name")}>
                <Input placeholder={`${translate("form.placeholders.enter")} ${translate("label.last_name")}`} />
              </Form.Item>

              {/* Jabatan: a free-text job title, Staf only ("Driver",
                  "Janitor"). Display only; the server ignores it for an
                  Instruktur. */}
              {isStaf && (
                <Form.Item name="job_title" label={translate("label.job_title")}>
                  <Input maxLength={60} placeholder={`${translate("form.placeholders.enter")} ${translate("label.job_title")}`} />
                </Form.Item>
              )}

              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: "email", message: translate("form.error.email") }]}
              >
                <Input placeholder={`${translate("form.placeholders.enter")} email`} />
              </Form.Item>

              <Form.Item name="gender" label={translate("label.gender")}>
                <Select placeholder={`${translate("form.placeholders.select")} ${translate("label.gender")}`}>
                  {Object.entries(Gender).map(([key, value]) => (
                    <Option key={key} value={value}>
                      {translate(`form.select.gender.${value}`)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="dob" label={translate("label.dob")}>
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={`${translate("form.placeholders.enter")} ${translate("label.dob")}`}
                  format={DEFAULT_DATE_FORMAT}
                  disabledDate={(current) => current && current > now().endOf("day")}
                />
              </Form.Item>
            </>
          )}
        </Form>

        {step === "confirm" && confirmCopy && (
          <div>
            <Typography.Paragraph>{confirmCopy.primary}</Typography.Paragraph>
            {confirmCopy.deactivatedInstructor && (
              <Typography.Paragraph type="warning">
                {translate("messages.info.existing_deactivated_instructor")}
              </Typography.Paragraph>
            )}
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={translate("label.name")}>{existing?.full_name || "-"}</Descriptions.Item>
              <Descriptions.Item label={translate("label.phone_number")}>{formatPhone(pendingPhone)}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default NewKaryawanModal;
